import { shareProductImageUrl } from './shareProductImage';

const SEO_ATTR = 'data-page-seo';
const SEO_READY_ATTR = 'data-seo-ready';

const DEFAULT_SITE = 'https://raheasy.co.ke';

export function getSiteOrigin() {
  const fromEnv = String(process.env.REACT_APP_SITE_URL || '').trim().replace(/\/+$/, '');
  if (fromEnv) return fromEnv;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return DEFAULT_SITE;
}

export function absoluteUrl(pathOrUrl) {
  const raw = String(pathOrUrl || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  return `${getSiteOrigin()}${path}`;
}

function upsertMeta(attr, key, content) {
  if (!content) return null;
  let el = document.querySelector(`meta[${attr}="${key}"][${SEO_ATTR}]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.setAttribute(SEO_ATTR, 'true');
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return el;
}

function upsertLink(rel, href) {
  if (!href) return null;
  let el = document.querySelector(`link[rel="${rel}"][${SEO_ATTR}]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute(SEO_ATTR, 'true');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return el;
}

function upsertJsonLd(id, data) {
  if (!data) return null;
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    el.setAttribute(SEO_ATTR, 'true');
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
  return el;
}

function removeJsonLd(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

export function markSeoReady(ready = true) {
  if (typeof document === 'undefined') return;
  if (ready) {
    document.documentElement.setAttribute(SEO_READY_ATTR, 'true');
  } else {
    document.documentElement.removeAttribute(SEO_READY_ATTR);
  }
}

export function clearPageSeo({ restoreTitle } = {}) {
  if (typeof document === 'undefined') return;
  if (restoreTitle != null) document.title = restoreTitle;
  document.querySelectorAll(`[${SEO_ATTR}]`).forEach((node) => node.remove());
  removeJsonLd('page-jsonld');
  markSeoReady(false);
}

/**
 * @param {object} options
 * @param {string} options.title
 * @param {string} [options.description]
 * @param {string} [options.canonicalPath] — path or absolute URL
 * @param {string} [options.ogImage]
 * @param {string} [options.ogType]
 * @param {boolean} [options.noindex]
 * @param {object} [options.jsonLd]
 */
export function applyPageSeo(options = {}) {
  if (typeof document === 'undefined') return () => {};

  const previousTitle = document.title;
  const {
    title,
    description,
    canonicalPath,
    ogImage,
    ogType = 'website',
    noindex = false,
    jsonLd,
  } = options;

  if (title) document.title = title;

  if (description) {
    upsertMeta('name', 'description', description.slice(0, 160));
  }

  if (noindex) {
    upsertMeta('name', 'robots', 'noindex, nofollow');
  } else {
    const robots = document.querySelector(`meta[name="robots"][${SEO_ATTR}]`);
    if (robots) robots.remove();
  }

  const canonical = canonicalPath ? absoluteUrl(canonicalPath) : '';
  if (canonical) upsertLink('canonical', canonical);

  if (title) {
    upsertMeta('property', 'og:title', title.replace(/\s*\|\s*Raheasy.*$/i, '').trim() || title);
  }
  if (description) upsertMeta('property', 'og:description', description.slice(0, 160));
  upsertMeta('property', 'og:type', ogType);
  if (canonical) upsertMeta('property', 'og:url', canonical);
  if (ogImage) {
    const img = absoluteUrl(ogImage);
    upsertMeta('property', 'og:image', img);
    upsertMeta('property', 'og:image:secure_url', img);
    upsertMeta('property', 'og:image:width', '1200');
    upsertMeta('property', 'og:image:height', '630');
    if (title) {
      upsertMeta(
        'property',
        'og:image:alt',
        title.replace(/\s*\|\s*Raheasy.*$/i, '').trim() || title
      );
    }
  }

  if (title) upsertMeta('name', 'twitter:title', title);
  if (description) upsertMeta('name', 'twitter:description', description.slice(0, 160));
  upsertMeta('name', 'twitter:card', ogImage ? 'summary_large_image' : 'summary');
  if (ogImage) upsertMeta('name', 'twitter:image', absoluteUrl(ogImage));

  if (jsonLd) upsertJsonLd('page-jsonld', jsonLd);

  markSeoReady(true);

  return () => clearPageSeo({ restoreTitle: previousTitle });
}

export function plainTextFromHtml(html) {
  return String(html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export const STATIC_PAGE_SEO = {
  '/': {
    title: 'Raheasy - Smart Shopping Platform',
    description:
      'Discover and compare deals, discounts, and promotions from trusted stores in Kenya. Browse categories, compare prices, and save with Raheasy.',
    canonicalPath: '/',
    ogType: 'website',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Raheasy',
      url: DEFAULT_SITE,
      description:
        'Compare deals and discounts from trusted stores in Kenya.',
      potentialAction: {
        '@type': 'SearchAction',
        target: `${DEFAULT_SITE}/?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },
  },
  '/about': {
    title: 'About Raheasy — Kenya\'s deal discovery platform',
    description:
      'Learn how Raheasy brings together deals, discounts, and promotions from trusted brands and stores across Kenya in one place.',
    canonicalPath: '/about',
  },
  '/help': {
    title: 'Help Centre | Raheasy',
    description:
      'Get answers about Raheasy, comparing deals, partner stores, and how to find the best prices in Kenya.',
    canonicalPath: '/help',
  },
  '/delivery': {
    title: 'Delivery information | Raheasy',
    description: 'Delivery information for purchases made through partner stores on Raheasy.',
    canonicalPath: '/delivery',
  },
  '/blog': {
    title: 'Raheasy Blog — tips & articles',
    description:
      'Articles and tips from Raheasy on smart shopping, savings, and product guides in Kenya.',
    canonicalPath: '/blog',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | Raheasy',
    description: 'How Raheasy collects, uses, and protects your information at raheasy.co.ke.',
    canonicalPath: '/privacy-policy',
  },
  '/outlets': {
    title: 'Stores & Merchants | Raheasy',
    description: 'Browse partner stores and merchants featured on Raheasy in Kenya.',
    canonicalPath: '/outlets',
  },
  '/new-deals': {
    title: 'New Deals | Raheasy',
    description: 'Newly listed deals on Raheasy. Browse the latest offers with the New badge.',
    canonicalPath: '/new-deals',
  },
  '/product-request': {
    title: 'Request a product | Raheasy',
    description: 'Can\'t find what you need? Request a product and our team will try to source it for you.',
    canonicalPath: '/product-request',
  },
  '/partner-with-us': {
    title: 'Partner With Us | Raheasy',
    description:
      'Register your business as a Raheasy vendor. Reach more shoppers and showcase your deals on Kenya\'s deal discovery platform.',
    canonicalPath: '/partner-with-us',
  },
};

/** Routes that should not be indexed (thin, duplicate, or utility). */
export const NOINDEX_PATH_PATTERNS = [
  /^\/checkout(?:\/|$)/,
  /^\/order-confirmation(?:\/|$)/,
  /^\/compare(?:\/|$)/,
  /^\/saved(?:\/|$)/,
  /^\/meta-oembed-demo(?:\/|$)/,
  /^\/warranty(?:\/|$)/,
];

export function isNoindexPath(pathname = '') {
  const path = String(pathname || '');
  return NOINDEX_PATH_PATTERNS.some((re) => re.test(path));
}

export function getNoindexSeo(pathname) {
  return {
    title: 'Raheasy',
    description: 'Raheasy smart shopping platform.',
    noindex: true,
    canonicalPath: pathname === '/warranty' ? '/blog' : pathname,
  };
}

export function buildProductSeo(product) {
  if (!product) return null;
  const name = String(product.name || 'Product').trim();
  const store = product.storeName || product.vendor?.name || product.vendor || '';
  const path = product.urlSlug
    ? `/product/${encodeURIComponent(String(product.urlSlug).trim())}`
    : null;
  const canonicalPath =
    path ||
    (product.id || product._id
      ? `/product/${encodeURIComponent(String(product.id || product._id))}`
      : '/');

  const description = store
    ? `Compare ${name} deals and prices at ${store}. Discover offers from trusted stores on Raheasy.`
    : `Compare ${name} deals and prices from trusted stores in Kenya on Raheasy.`;

  const image = shareProductImageUrl(product);

  return {
    title: `${name} — compare deals | Raheasy`,
    description: description.slice(0, 160),
    canonicalPath,
    ogImage: image,
    ogType: 'product',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name,
      description: description.slice(0, 300),
      image: image ? [absoluteUrl(image)] : undefined,
      brand: product.brand ? { '@type': 'Brand', name: product.brand } : undefined,
      offers: product.price != null
        ? {
            '@type': 'Offer',
            price: Number(product.price),
            priceCurrency: 'KES',
            availability:
              product.inStock === false
                ? 'https://schema.org/OutOfStock'
                : 'https://schema.org/InStock',
            url: absoluteUrl(canonicalPath),
          }
        : undefined,
    },
  };
}

export function buildCategorySeo(category, subcategoryName, pathname) {
  if (!category) return null;
  const catName = String(category.name || 'Category').trim();
  const titleName = subcategoryName
    ? `${String(subcategoryName).trim()} — ${catName}`
    : catName;
  const slug = category.slug || catName.toLowerCase().replace(/\s+/g, '-');
  const canonicalPath = pathname || `/category/${encodeURIComponent(slug)}`;

  const description = subcategoryName
    ? `Browse ${subcategoryName} deals in ${catName}. Compare prices from trusted stores on Raheasy Kenya.`
    : `Browse ${catName} deals and discounts. Compare prices from trusted stores in Kenya on Raheasy.`;

  return {
    title: `${titleName} deals & discounts | Raheasy`,
    description: description.slice(0, 160),
    canonicalPath,
    ogImage: category.image || '',
    ogType: 'website',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${titleName} deals`,
      description: description.slice(0, 300),
      url: absoluteUrl(canonicalPath),
    },
  };
}
